import { useState } from "react";
import { X, Palette, Check } from "lucide-react";
import { useTheme, type BrandTheme, type CustomColors } from "./theme-provider";
import { ThemeToggle } from "./theme-toggle";
import { cn } from "@/lib/utils";

const BRAND_THEMES: {
  id: BrandTheme;
  name: string;
  description: string;
  preview: { accent: string; sidebar: string; surface: string };
}[] = [
  {
    id: "default",
    name: "ProClaw",
    description: "Warm green, cream tones",
    preview: { accent: "#10a37f", sidebar: "#fafaf8", surface: "#efede8" },
  },
  {
    id: "claude",
    name: "Claude",
    description: "Terracotta, warm parchment",
    preview: { accent: "#c96442", sidebar: "#f5f3ed", surface: "#f0ede6" },
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    description: "Clean green, cool grays",
    preview: { accent: "#10a37f", sidebar: "#f9f9f9", surface: "#ececed" },
  },
  {
    id: "custom",
    name: "Custom",
    description: "Pick your own colors",
    preview: { accent: "#6366f1", sidebar: "#f0f0ff", surface: "#eef" },
  },
];

export function ThemePicker({ onClose }: { onClose: () => void }) {
  const { brand, setBrand, customColors, setCustomColors } = useTheme();
  const [draft, setDraft] = useState<CustomColors>(customColors);

  const applyCustom = () => {
    setBrand("custom");
    setCustomColors(draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-[min(420px,calc(100vw-2rem))] max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-background shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2.5">
            <Palette size={18} className="text-accent" />
            <h2 className="text-[15px] font-semibold text-text-primary">Appearance</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Mode toggle */}
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
              Mode
            </label>
            <ThemeToggle />
          </div>

          {/* Brand themes */}
          <div>
            <label className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
              Theme
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {BRAND_THEMES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    if (t.id === "custom") {
                      applyCustom();
                    } else {
                      setBrand(t.id);
                    }
                  }}
                  className={cn(
                    "relative flex flex-col items-start rounded-xl border p-3 text-left transition-all",
                    brand === t.id
                      ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                      : "border-border hover:border-accent/40 hover:bg-surface",
                  )}
                >
                  {brand === t.id && (
                    <div className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-accent">
                      <Check size={10} className="text-white" />
                    </div>
                  )}
                  {/* Color preview dots */}
                  <div className="mb-2 flex gap-1.5">
                    <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: t.preview.accent }} />
                    <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: t.preview.sidebar }} />
                    <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: t.preview.surface }} />
                  </div>
                  <span className="text-[13px] font-medium text-text-primary">{t.name}</span>
                  <span className="text-[11px] text-text-muted">{t.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom color editor — only shown when custom is selected */}
          {brand === "custom" && (
            <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted">
                Custom Colors
              </p>
              <ColorRow label="Accent" value={draft.accent} onChange={(v) => setDraft({ ...draft, accent: v })} />
              <ColorRow label="Accent Hover" value={draft.accentHover} onChange={(v) => setDraft({ ...draft, accentHover: v })} />
              <ColorRow label="Sidebar (Light)" value={draft.sidebar} onChange={(v) => setDraft({ ...draft, sidebar: v })} />
              <ColorRow label="Surface (Light)" value={draft.surface} onChange={(v) => setDraft({ ...draft, surface: v })} />
              <ColorRow label="Sidebar (Dark)" value={draft.sidebarDark} onChange={(v) => setDraft({ ...draft, sidebarDark: v })} />
              <ColorRow label="Surface (Dark)" value={draft.surfaceDark} onChange={(v) => setDraft({ ...draft, surfaceDark: v })} />
              <button
                onClick={applyCustom}
                className="mt-2 w-full rounded-lg bg-accent px-3 py-2 text-[13px] font-medium text-white hover:bg-accent-hover transition-colors"
              >
                Apply Colors
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-text-secondary">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 w-6 cursor-pointer rounded border-0 p-0"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[80px] rounded-md border border-border bg-background px-2 py-1 text-[11px] font-mono text-text-primary focus:outline-none focus:border-accent"
        />
      </div>
    </div>
  );
}
